import styled from "styled-components";
import { FC, useEffect, useRef } from "react";

import { BaseComponentProps } from "../types";
import { primaryActive, primaryInactive } from "../../typography/alpha";
import { FontWeight, ItemSize, TokenColors } from "../../types/components";
import { getFontSize } from "../../utils";

import { ReactComponent as CheckboxOffIcon } from "../../assets/icons/checkbox-off.svg";
import { ReactComponent as CheckboxOnIcon } from "../../assets/icons/checkbox-on.svg";
import { ReactComponent as CheckboxIndeterminateIcon } from "../../assets/icons/checkbox-indeterminate-solid.svg";

export enum CheckboxTestIds {
  Container = "checkbox",
}

const Input = styled.input`
  position: absolute;
  height: 0;
  width: 0;
  opacity: 0;
  z-index: -1;
`;

type MarkerProps = {
  $size?: ItemSize;
  $borderColor: TokenColors;
  $checkedColor: TokenColors;
};

const Marker = styled.div<MarkerProps>`
  display: block;
  position: relative;
  width: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  height: ${({ $size }): number =>
    $size === "medium" || $size === "large" ? 24 : 16}px;
  overflow: hidden;
  --main-color: var(--color-${({ $checkedColor }) => $checkedColor});

  .icon-off,
  .icon-on,
  .icon-indeterminate {
    position: absolute;
    width: 133.33%;
    height: 133.33%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    transition: opacity 150ms ease-in-out;
  }

  .icon-on,
  .icon-indeterminate {
    opacity: 0;
  }
  .icon-off {
    opacity: 1;
    --main-color: var(--color-${({ $borderColor }) => $borderColor});
  }

  ${Input}:checked + & {
    .icon-off {
      opacity: 0;
    }
    .icon-on {
      opacity: 1;
    }
  }

  ${Input}:indeterminate + & {
    .icon-off {
      opacity: 0;
    }
    .icon-indeterminate {
      opacity: 1;
    }
  }

  ${Input}:disabled + & {
    --main-color: var(--color-primary-200);
    cursor: not-allowed;
  }
`;

type LabelProps = {
  $disabled?: boolean;
  $size: ItemSize;
  $fontWeight: FontWeight;
};

const Label = styled.label<LabelProps>`
  display: grid;
  grid-template-columns: min-content max-content;
  grid-template-areas: "marker label";
  align-items: center;
  width: max-content;
  ${({ $disabled }) =>
    $disabled ? primaryInactive.subtitle3 : primaryActive.subtitle3};
  cursor: ${({ $disabled }): string => ($disabled ? "not-allowed" : "pointer")};
  font-size: ${({ $size }): number => getFontSize($size)}px;
  font-weight: var(
    --font-weight-${({ $fontWeight }): FontWeight => $fontWeight}
  );

  span {
    grid-area: label;
    user-select: none;
    margin-left: 4px;
  }

  ${Marker} {
    grid-area: marker;
  }
`;

type Props = Omit<BaseComponentProps<"input">, "size"> & {
  id: string;
  testId?: string;
  className?: string;
  label?: string;
  size?: ItemSize;
  fontWeight?: FontWeight;
  indeterminate?: boolean;
  borderColor?: TokenColors;
  checkedColor?: TokenColors;
};

export const Checkbox: FC<Props> = ({
  id,
  testId,
  className,
  label,
  size = "regular" as ItemSize,
  fontWeight = "regular" as FontWeight,
  disabled,
  indeterminate,
  title,
  borderColor = "primary-400",
  checkedColor = "secondary-800",
  ...args
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref?.current) {
      ref.current.indeterminate = Boolean(indeterminate);
      if (ref.current.indeterminate) {
        ref.current.checked = false;
      }
    }
  }, [indeterminate]);

  return (
    <Label
      htmlFor={id}
      data-testid={testId ?? CheckboxTestIds.Container}
      $disabled={disabled}
      $size={size}
      $fontWeight={fontWeight}
      className={className}
      title={title}
    >
      {Boolean(label) && <span>{label}</span>}
      <Input
        id={id}
        aria-label={label}
        ref={ref}
        type="checkbox"
        disabled={disabled}
        {...args}
      />
      <Marker
        $size={size}
        $borderColor={borderColor}
        $checkedColor={checkedColor}
      >
        <CheckboxOffIcon className="icon-off" />
        <CheckboxOnIcon className="icon-on" />
        <CheckboxIndeterminateIcon className="icon-indeterminate" />
      </Marker>
    </Label>
  );
};
